import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import type { ConfirmRequest } from "@/symphony/actions";
import { createSymphonyBridge } from "@/symphony/bridge";
import { canMountSymphonyWidget, readSymphonyRuntimeConfig } from "@/symphony/flag";
import {
  clearAgentCreate,
  clearDemoCreate,
  clearNumberAttach,
  publishAgentCreate,
  publishDemoCreate,
  publishNumberAttach,
} from "@/symphony/pending";
import { mountSymphonyLoader, pushSymphonySession } from "@/symphony/session";
import { loadSymphonyAccountContext, type SymphonyReadClient } from "../../supabase/functions/_shared/symphonyAccountContext";

export function SymphonyWidget() {
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const config = readSymphonyRuntimeConfig();
  const tokenRef = useRef<string | null>(session?.access_token ?? null);
  tokenRef.current = session?.access_token ?? null;
  const sessionReady = Boolean(session?.access_token && user);
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const confirmResolve = useRef<((accepted: boolean) => void) | null>(null);

  useEffect(() => {
    if (!session?.access_token || !user) return;
    const ready = canMountSymphonyWidget({
      envEnabled: config.enabled ? "true" : undefined,
      allowedUserIds: config.allowedUserIds,
      publishableKey: config.publishableKey,
      loaderUrl: config.loaderUrl,
      gateway: config.gateway,
      userId: user.id,
      appMetadata: user.app_metadata as Record<string, unknown> | null,
      userMetadata: user.user_metadata as Record<string, unknown> | null,
    });
    if (!ready) return;

    const ask = (request: ConfirmRequest) => new Promise<boolean>((resolve) => {
      if (confirmResolve.current) confirmResolve.current(false);
      confirmResolve.current = resolve;
      setConfirm(request);
    });

    const removeBridge = createSymphonyBridge({
      gateway: config.gateway,
      getAccountContext: () => loadSymphonyAccountContext(supabase as unknown as SymphonyReadClient, user.id),
      host: {
        navigate: (path) => navigate(path),
        confirm: ask,
        openAgentCreate: (prefill) => {
          clearAgentCreate();
          publishAgentCreate(prefill);
        },
        openDemoCreate: (prefill) => {
          clearDemoCreate();
          publishDemoCreate(prefill);
        },
        openNumberAttach: (prefill) => {
          clearNumberAttach();
          publishNumberAttach(prefill);
        },
        isAdmin: async () => {
          const { data, error } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", user.id)
            .eq("role", "admin")
            .maybeSingle();
          return !error && Boolean(data);
        },
      },
    });

    const removeLoader = mountSymphonyLoader({
      publishableKey: config.publishableKey,
      loaderUrl: config.loaderUrl,
      gateway: config.gateway,
      getAccessToken: () => tokenRef.current,
      onReady: () => {
        const hostWindow = window as Window & { SpeakSymphony?: { registerClientTools?: () => void } };
        hostWindow.SpeakSymphony?.registerClientTools?.();
        pushSymphonySession({
          access_token: tokenRef.current ?? session.access_token,
          expires_at: session.expires_at,
          user: { id: user.id },
        });
      },
    });

    return () => {
      removeLoader();
      removeBridge();
      if (confirmResolve.current) {
        confirmResolve.current(false);
        confirmResolve.current = null;
      }
    };
    // Install once the session exists. Later token refreshes are pushed below
    // and must not tear the loader down.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionReady, user?.id, config.publishableKey, config.loaderUrl, config.gateway, config.enabled, config.allowedUserIds]);

  useEffect(() => {
    if (!session?.access_token || !user) return;
    pushSymphonySession({
      access_token: session.access_token,
      expires_at: session.expires_at,
      user: { id: user.id },
    });
  }, [session?.access_token, session?.expires_at, user]);

  if (!confirm) return null;

  const settle = (accepted: boolean) => {
    confirmResolve.current?.(accepted);
    confirmResolve.current = null;
    setConfirm(null);
  };

  return (
    <div className="fixed inset-0 z-[2147483600] flex items-center justify-center bg-black/60 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="symphony-confirm-title"
        data-testid="symphony-confirm"
        className="w-full max-w-md rounded-lg border bg-background p-6 text-foreground shadow-lg"
      >
        <h2 id="symphony-confirm-title" className="text-lg font-semibold">{confirm.title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{confirm.description}</p>
        {confirm.fields.length > 0 && (
          <dl className="mt-4 space-y-2 text-sm">
            {confirm.fields.map((field) => (
              <div key={field.label} className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{field.label}</dt>
                <dd className="text-right font-medium">{field.value}</dd>
              </div>
            ))}
          </dl>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => settle(false)}>Cancel</Button>
          <Button type="button" onClick={() => settle(true)}>{confirm.confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
}
